import { api } from './client';
import type { ChatbotPrompt, ChatbotService, ChatbotSettings, Feature, FeatureStatus, Permission, PermissionCode, Phase, PhaseContractTag } from '../types';
import type {
  AdminFeature,
  AdminFeatureImage,
  AdminPortal,
  AdminProject,
  AdminRole,
  AdminUser,
  AuditLog,
  AuditLogQuery,
  AuditLogSetting,
  AuthSettings,
  AuthSettingsUpdate,
  BulkFeatureItem,
  BulkImportResult,
  CreateContractTagPayload,
  CreateFeaturePayload,
  CreatePhasePayload,
  CreatePortalPayload,
  CreateProjectPayload,
  CreateRolePayload,
  CreateUserPayload,
  Page,
  PageQuery,
  PortalProjectLink,
  ProjectChatSettings,
  ProjectChatSettingsUpdate,
  ProjectMember,
  UpdateContractTagPayload,
  UpdateFeaturePayload,
  UpdatePhasePayload,
  UpdatePortalPayload,
  UpdateProjectPayload,
  UpdateRolePayload,
  UpdateUserPayload,
} from '../types/admin';

/**
 * Lớp gọi API dành riêng cho khu vực quản trị (/admin). Bổ sung cho
 * '../api/endpoints' (dùng chung), KHÔNG chỉnh sửa file đó.
 * Bám sát đúng hợp đồng thực tế của apps/api/src/routes/{users,roles,
 * projects,phases,features,images}.ts (danh sách trả về dạng { items: [...] },
 * chi tiết 1 bản ghi trả về object trần).
 * Mọi hàm throw lỗi gốc (axios) cho phía gọi xử lý bằng getApiErrorMessage.
 */

interface ItemsResponse<T> {
  items: T[];
}

/* -------------------------------------------------------------------------- */
/*  Users                                                                     */
/* -------------------------------------------------------------------------- */

export const usersApi = {
  async list(query: PageQuery = {}): Promise<Page<AdminUser>> {
    const res = await api.get<Page<AdminUser>>('/users', { params: query });
    return res.data;
  },
  async create(payload: CreateUserPayload): Promise<AdminUser> {
    const res = await api.post<AdminUser>('/users', payload);
    return res.data;
  },
  async update(id: string, payload: UpdateUserPayload): Promise<AdminUser> {
    const res = await api.put<AdminUser>(`/users/${id}`, payload);
    return res.data;
  },
  /** Xóa mềm (deleted_at). */
  async remove(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },
  async setActive(id: string, isActive: boolean): Promise<AdminUser> {
    const res = await api.put<AdminUser>(`/users/${id}/status`, { is_active: isActive });
    return res.data;
  },
  /** Bật/tắt hiển thị hướng dẫn sử dụng (onboarding tour) cho một tài khoản. */
  async setOnboardingTour(id: string, show: boolean): Promise<AdminUser> {
    const res = await api.put<AdminUser>(`/users/${id}/onboarding-tour`, { show_onboarding_tour: show });
    return res.data;
  },
  async assignRole(userId: string, roleId: string): Promise<AdminUser> {
    const res = await api.post<AdminUser>(`/users/${userId}/roles`, { role_id: roleId });
    return res.data;
  },
  async removeRole(userId: string, roleId: string): Promise<AdminUser> {
    const res = await api.delete<AdminUser>(`/users/${userId}/roles/${roleId}`);
    return res.data;
  },
};

/* -------------------------------------------------------------------------- */
/*  Roles & Permissions                                                       */
/* -------------------------------------------------------------------------- */

export const rolesApi = {
  async list(query: PageQuery = {}): Promise<Page<AdminRole>> {
    const res = await api.get<Page<AdminRole>>('/roles', { params: query });
    return res.data;
  },
  async create(payload: CreateRolePayload): Promise<AdminRole> {
    const res = await api.post<AdminRole>('/roles', payload);
    return res.data;
  },
  async update(id: string, payload: UpdateRolePayload): Promise<AdminRole> {
    const res = await api.put<AdminRole>(`/roles/${id}`, payload);
    return res.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/roles/${id}`);
  },
  /**
   * Gán bộ quyền đầy đủ cho vai trò. API chỉ hỗ trợ gán/gỡ TỪNG quyền một
   * (POST .../permissions {code} / DELETE .../permissions/:code) — không có
   * endpoint dạng mảng. Hàm này tính diff giữa `previousCodes` và `codes` rồi
   * gọi song song các request cần thiết.
   */
  async setPermissions(
    roleId: string,
    codes: PermissionCode[],
    previousCodes: PermissionCode[] = [],
  ): Promise<void> {
    const prevSet = new Set(previousCodes);
    const nextSet = new Set(codes);
    const toAdd = codes.filter((c) => !prevSet.has(c));
    const toRemove = previousCodes.filter((c) => !nextSet.has(c));
    await Promise.all([
      ...toAdd.map((code) => api.post(`/roles/${roleId}/permissions`, { code })),
      ...toRemove.map((code) => api.delete(`/roles/${roleId}/permissions/${code}`)),
    ]);
  },
  /** Danh sách status của `features` mà role được phép xem. [] = xem tất cả. */
  async getFeatureStatusFilter(roleId: string): Promise<FeatureStatus[]> {
    const res = await api.get<{ statuses: FeatureStatus[] }>(`/roles/${roleId}/feature-status-filter`);
    return res.data.statuses;
  },
  /** Thay thế toàn bộ tập status được phép xem. [] = gỡ mọi giới hạn. */
  async setFeatureStatusFilter(roleId: string, statuses: FeatureStatus[]): Promise<FeatureStatus[]> {
    const res = await api.put<{ statuses: FeatureStatus[] }>(`/roles/${roleId}/feature-status-filter`, {
      statuses,
    });
    return res.data.statuses;
  },
};

export const permissionsApi = {
  /** Danh sách toàn bộ quyền hệ thống. */
  async list(): Promise<Permission[]> {
    const res = await api.get<ItemsResponse<Permission>>('/permissions');
    return res.data.items;
  },
};

/* -------------------------------------------------------------------------- */
/*  Projects                                                                  */
/* -------------------------------------------------------------------------- */

export const adminProjectApi = {
  async list(query: PageQuery = {}): Promise<Page<AdminProject>> {
    const res = await api.get<Page<AdminProject>>('/projects', { params: query });
    return res.data;
  },
  async get(id: string): Promise<AdminProject> {
    const res = await api.get<AdminProject>(`/projects/${id}`);
    return res.data;
  },
  async create(payload: CreateProjectPayload): Promise<AdminProject> {
    const res = await api.post<AdminProject>('/projects', payload);
    return res.data;
  },
  async update(id: string, payload: UpdateProjectPayload): Promise<AdminProject> {
    const res = await api.put<AdminProject>(`/projects/${id}`, payload);
    return res.data;
  },
  /** Xóa mềm dự án. */
  async remove(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },
  async listUsers(projectId: string): Promise<ProjectMember[]> {
    const res = await api.get<ItemsResponse<ProjectMember>>(`/projects/${projectId}/users`);
    return res.data.items;
  },
  async assignUser(projectId: string, userId: string, roleInProject?: string): Promise<ProjectMember> {
    const res = await api.post<ProjectMember>(`/projects/${projectId}/users`, {
      user_id: userId,
      ...(roleInProject ? { role_in_project: roleInProject } : {}),
    });
    return res.data;
  },
  async removeUser(projectId: string, userId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/users/${userId}`);
  },
};

/* -------------------------------------------------------------------------- */
/*  Phases                                                                    */
/* -------------------------------------------------------------------------- */

export const adminPhaseApi = {
  async listByProject(projectId: string, query: PageQuery = {}): Promise<Page<Phase>> {
    const res = await api.get<Page<Phase>>(`/projects/${projectId}/phases`, { params: query });
    return res.data;
  },
  async create(projectId: string, payload: CreatePhasePayload): Promise<Phase> {
    const res = await api.post<Phase>(`/projects/${projectId}/phases`, payload);
    return res.data;
  },
  async update(id: string, payload: UpdatePhasePayload): Promise<Phase> {
    const res = await api.put<Phase>(`/phases/${id}`, payload);
    return res.data;
  },
  /** Xóa mềm giai đoạn. */
  async remove(id: string): Promise<void> {
    await api.delete(`/phases/${id}`);
  },
};

/* -------------------------------------------------------------------------- */
/*  Contract tags (theo giai đoạn)                                            */
/* -------------------------------------------------------------------------- */

export const adminContractTagApi = {
  async create(phaseId: string, payload: CreateContractTagPayload): Promise<PhaseContractTag> {
    const res = await api.post<PhaseContractTag>(`/phases/${phaseId}/contract-tags`, payload);
    return res.data;
  },
  async update(id: string, payload: UpdateContractTagPayload): Promise<PhaseContractTag> {
    const res = await api.patch<PhaseContractTag>(`/contract-tags/${id}`, payload);
    return res.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/contract-tags/${id}`);
  },
};

/* -------------------------------------------------------------------------- */
/*  Features & Images                                                         */
/* -------------------------------------------------------------------------- */

export const adminFeatureApi = {
  async listByPhase(phaseId: string, query: PageQuery = {}): Promise<Page<AdminFeature>> {
    const res = await api.get<Page<AdminFeature>>(`/phases/${phaseId}/features`, { params: query });
    return res.data;
  },
  async create(phaseId: string, payload: CreateFeaturePayload): Promise<AdminFeature> {
    const res = await api.post<AdminFeature>(`/phases/${phaseId}/features`, payload);
    return res.data;
  },
  async update(id: string, payload: UpdateFeaturePayload): Promise<Feature> {
    const res = await api.put<Feature>(`/features/${id}`, payload);
    return res.data;
  },
  /**
   * Import (tạo hàng loạt) tính năng vào 1 giai đoạn. Backend chỉ thêm mới,
   * validate từng item và trả về { created, errors }. Trả 400 nếu không tạo
   * được item nào (axios sẽ throw — phía gọi bắt để đọc data.errors).
   */
  async bulkCreate(phaseId: string, features: BulkFeatureItem[]): Promise<BulkImportResult> {
    const res = await api.post<BulkImportResult>(`/phases/${phaseId}/features/bulk`, { features });
    return res.data;
  },
  /** Xóa mềm tính năng. */
  async remove(id: string): Promise<void> {
    await api.delete(`/features/${id}`);
  },
  async listImages(featureId: string): Promise<AdminFeatureImage[]> {
    const res = await api.get<ItemsResponse<AdminFeatureImage>>(`/features/${featureId}/images`);
    return res.data.items;
  },
  /**
   * API chỉ nhận MỘT ảnh mỗi request (field multipart "file"). Để tải lên
   * nhiều ảnh, gửi tuần tự từng request và gộp kết quả.
   */
  async uploadImages(featureId: string, files: File[]): Promise<AdminFeatureImage[]> {
    const uploaded: AdminFeatureImage[] = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<AdminFeatureImage>(`/features/${featureId}/images`, form);
      uploaded.push(res.data);
    }
    return uploaded;
  },
  /** Xóa mềm một hình ảnh. */
  async removeImage(imageId: string): Promise<void> {
    await api.delete(`/images/${imageId}`);
  },
};

/* -------------------------------------------------------------------------- */
/*  Audit log                                                                 */
/* -------------------------------------------------------------------------- */

export const auditLogApi = {
  async list(query: AuditLogQuery = {}): Promise<AuditLog[]> {
    const res = await api.get<ItemsResponse<AuditLog> | AuditLog[]>('/audit-logs', { params: query });
    // Phòng trường hợp route chưa hoàn thiện trả về mảng trần thay vì { items }.
    return Array.isArray(res.data) ? res.data : res.data.items;
  },
};

/* -------------------------------------------------------------------------- */
/*  Audit log settings (bật/tắt theo entity_type + action)                    */
/* -------------------------------------------------------------------------- */

export const auditSettingsApi = {
  async list(): Promise<AuditLogSetting[]> {
    const res = await api.get<ItemsResponse<AuditLogSetting>>('/audit-settings');
    return res.data.items;
  },
  async update(settings: { entity_type: string; action: string; enabled: boolean }[]): Promise<AuditLogSetting[]> {
    const res = await api.put<ItemsResponse<AuditLogSetting>>('/audit-settings', { settings });
    return res.data.items;
  },
};

/* -------------------------------------------------------------------------- */
/*  Cấu hình đăng nhập Google                                                 */
/* -------------------------------------------------------------------------- */

export const authSettingsApi = {
  /** chat_webhook_url trả về LUÔN ở dạng đã che, không bao giờ là URL thật. */
  async get(): Promise<AuthSettings> {
    const res = await api.get<AuthSettings>('/auth-settings');
    return res.data;
  },
  /** PATCH từng phần — chỉ gửi các trường thay đổi, trường bỏ qua giữ nguyên giá trị cũ. */
  async update(payload: AuthSettingsUpdate): Promise<AuthSettings> {
    const res = await api.put<AuthSettings>('/auth-settings', payload);
    return res.data;
  },
  /** Gửi thử tới webhook ĐÃ LƯU trong DB — không nhận URL trong body. */
  async testWebhook(): Promise<void> {
    await api.post('/auth-settings/test-webhook');
  },
};

/* -------------------------------------------------------------------------- */
/*  Portals (cổng truy cập theo domain)                                      */
/* -------------------------------------------------------------------------- */

export const portalsApi = {
  async list(query: PageQuery = {}): Promise<Page<AdminPortal>> {
    const res = await api.get<Page<AdminPortal>>('/portals', { params: query });
    return res.data;
  },
  async create(payload: CreatePortalPayload): Promise<AdminPortal> {
    const res = await api.post<AdminPortal>('/portals', payload);
    return res.data;
  },
  async update(id: string, payload: UpdatePortalPayload): Promise<AdminPortal> {
    const res = await api.put<AdminPortal>(`/portals/${id}`, payload);
    return res.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/portals/${id}`);
  },
  async uploadLogo(id: string, file: File): Promise<AdminPortal> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<AdminPortal>(`/portals/${id}/logo`, form);
    return res.data;
  },
  async removeLogo(id: string): Promise<AdminPortal> {
    const res = await api.delete<AdminPortal>(`/portals/${id}/logo`);
    return res.data;
  },
  async uploadFavicon(id: string, file: File): Promise<AdminPortal> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<AdminPortal>(`/portals/${id}/favicon`, form);
    return res.data;
  },
  async removeFavicon(id: string): Promise<AdminPortal> {
    const res = await api.delete<AdminPortal>(`/portals/${id}/favicon`);
    return res.data;
  },
  async listProjects(portalId: string): Promise<PortalProjectLink[]> {
    const res = await api.get<ItemsResponse<PortalProjectLink>>(`/portals/${portalId}/projects`);
    return res.data.items;
  },
  async assignProject(portalId: string, projectId: string): Promise<PortalProjectLink> {
    const res = await api.post<PortalProjectLink>(`/portals/${portalId}/projects`, { project_id: projectId });
    return res.data;
  },
  async removeProject(portalId: string, projectId: string): Promise<void> {
    await api.delete(`/portals/${portalId}/projects/${projectId}`);
  },
};

/* ------------------------------ Chatbot admin ------------------------------ */

export const adminChatbotApi = {
  async listServices(): Promise<ChatbotService[]> {
    const res = await api.get<{ items: ChatbotService[] }>('/chatbot-services');
    return res.data.items;
  },
  async updateService(
    id: string,
    body: { label?: string; enabled?: boolean; sort_order?: number },
  ): Promise<ChatbotService> {
    const res = await api.put<ChatbotService>(`/chatbot-services/${id}`, body);
    return res.data;
  },
  async listPrompts(): Promise<ChatbotPrompt[]> {
    const res = await api.get<{ items: ChatbotPrompt[] }>('/chatbot-prompts');
    return res.data.items;
  },
  async updatePrompt(stepKey: string, message: string): Promise<ChatbotPrompt> {
    const res = await api.put<ChatbotPrompt>(`/chatbot-prompts/${stepKey}`, { message });
    return res.data;
  },
  async getSettings(): Promise<ChatbotSettings> {
    const res = await api.get<ChatbotSettings>('/chatbot-settings');
    return res.data;
  },
  async updateSettings(body: {
    ai_enabled?: boolean;
    ai_max_turns?: number;
    ai_system_prompt?: string;
  }): Promise<ChatbotSettings> {
    const res = await api.put<ChatbotSettings>('/chatbot-settings', body);
    return res.data;
  },
};

/* -------------------------------------------------------------------------- */
/*  Cấu hình thông báo Google Chat theo dự án                                 */
/* -------------------------------------------------------------------------- */

export const projectChatSettingsApi = {
  /** webhook_url trả về LUÔN ở dạng đã che, không bao giờ là URL thật. */
  async get(projectId: string): Promise<ProjectChatSettings> {
    const res = await api.get<ProjectChatSettings>(`/projects/${projectId}/chat-settings`);
    return res.data;
  },
  async update(projectId: string, payload: ProjectChatSettingsUpdate): Promise<ProjectChatSettings> {
    const res = await api.put<ProjectChatSettings>(`/projects/${projectId}/chat-settings`, payload);
    return res.data;
  },
  /** Gửi thử tới webhook ĐÃ LƯU trong DB — không nhận URL trong body. */
  async test(projectId: string): Promise<void> {
    await api.post(`/projects/${projectId}/chat-settings/test`);
  },
};
